import React, { useCallback, useEffect, useState, useRef, useContext, forwardRef, useImperativeHandle, useLayoutEffect, useMemo } from 'react';
import { socket } from '../../context/socket';
import Message from '../message/Message';
import { GalaxyChatMediaContext } from './GalaxyChatMedia';
import ChatBoxBottom from '../chatBoxBottom/ChatBoxBottom';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';
import useClickOutside from '../../clickOutside';
import { handleEmoji } from '../../utils/handleEmojis';
import { useIsTyping } from 'use-is-typing';
import useGalaxyTyping from '../../hooks/useGalaxyTyping';
import TypingIndicator from '../typingIndicator/TypingIndicator';
import useScopedChatRecorder from '../../hooks/useScopedChatRecorder';
import { acquireGalaxyChat } from '../../utils/galaxyChatParticipation';
import { reconcileGalaxyMessage } from '../../utils/galaxyChatMessages';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import ChatBoxMiddle from '../../components/chatBoxMiddle/ChatBoxMiddle';
import { ViewPortContext } from '../../context/ViewPortContext';
import handleFileInput from '../../utils/handleFileInput';
import { debounce } from '../../context/ViewPortContext';
import WelcomeMessage from '../../components/welcomeMessage/WelcomeMessage';
import AdRotator from '../adRotator/AdRotator';
import { v4 as uuidv4 } from 'uuid';
import { MAX_VIDEO_SIZE, MAX_IMAGE_SIZE } from '../../config/constants';
import { INCOMING_MESSAGE_BL_URL } from '../../utils/constants';
import './galaxyChatBox.css';
import useGalaxyChatScroll, { galaxyScrollIdentity } from './useGalaxyChatScroll';

// NEW (Better-than-FAANG+ uploader)
import {
  createMediaUploader,
  DEFAULT_VIDEO_MIME_TYPES,
  MediaUploadError,
} from '../../utils/mediaUpload';

export { reconcileGalaxyMessage } from '../../utils/galaxyChatMessages';

const MESSAGE_PARTICLE_COUNT = 100;
const maxVideoSize = MAX_VIDEO_SIZE;
const maxImageSize = MAX_IMAGE_SIZE;

const getStableId = (value) => {
  if (!value) return '';
  return String(value._id || value.id || value);
};

const listIncludesPlayer = (list = [], playerId) =>
  list.some((item) => getStableId(item) === String(playerId));

const GalaxyChatBox = forwardRef(({
  galaxyId,
  senderId,
  senderProfilePhoto,
  senderPlayerId,
  galaxyMembers,
  subscriptionStatus = 'Subscribe',
  setProgress,
  galaxyChatOnlinePlayers,
  isPlayerInRole,
  setMessages,
  messages = [],
  isChatHistoryLoading = false,
  isChatHistoryLoaded = false,
  chatHistoryError = null,
  onRetryChatHistory,
  isGalaxyMember = false,
  onSubscribe,
  onCancelRequest,
  onUnsubscribe,
  membershipButtonLabel,
  className,
  galaxyName = '',
  variant = 'galaxy',
  active = true,
  recordReference = '',
  onBeforeCapture,
  onAccessDenied,
}, ref) => {
  const isRadio = variant === 'radio';
  const { player } = useContext(AuthContext);
  const { width } = useContext(ViewPortContext);

  const [messageText, setMessageText] = useState('');
  const from = 'galaxyChat';
  const [voiceMemo, setVoiceMemo] = useState(false);
  const [picker, setPicker] = useState(false);
  const middleRef = useRef(null);
  const [isTyping, register] = useIsTyping({ timeout: 300 });
  const [isTypingDisplay, SetIsTypingDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recorder = useScopedChatRecorder({ scoped: isRadio,
    scope: `${galaxyId}:${senderId}:${player?.token}:${isGalaxyMember}`,
    onBeforeCapture: () => {
      stopChatMedia();
      onBeforeCapture?.();
    } });
  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl,
    permissionPending, captureError, cancelRecording } = recorder;
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [error, setError] = useState('');
  const [offlineMembers, setOfflineMembers] = useState([]);

  const mobileView = useMobileView();
  const menu = useRef(null);
  const rootRef = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const fileInput = useRef(null);
  const [cursorPosition, setCursorPosition] = useState();
  const [file, setFile] = useState(null);
  const textRef = useRef(null);
  const messageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const chatContentRef = useRef(null);
  const messageSoundRef = useRef();
  const sendingRef = useRef(false);
  const chatScopeRef = useRef('');
  const readingScope = `${galaxyId}:${senderId}:${player?.token}:${subscriptionStatus}`;
  const operation = useRef({ scope: readingScope, active, generation: 0 });
  if (operation.current.scope !== readingScope || operation.current.active !== active) {
    operation.current = { scope: readingScope, active, generation: operation.current.generation + 1 };
  }
  const chatScope = `${readingScope}:${operation.current.generation}`;
  chatScopeRef.current = chatScope;
  useEffect(() => () => {
    chatScopeRef.current = '';
  }, []);

  const attachChatFile = useCallback(
    (incomingFile, { source = 'picker' } = {}) => {
      const result = handleFileInput(
        { target: { files: incomingFile ? [incomingFile] : [] } },
        setError,
        setFile,
        maxVideoSize,
        maxImageSize,
      );
      return {
        accepted: result?.accepted === true,
        errorCode: result?.errorCode || null,
        file: result?.file || null,
        source,
        message: result?.message,
      };
    },
    [],
  );

  const clearChatAttachment = useCallback(() => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
  }, []);

  // Uploader (singleton via ref)
  const uploaderRef = useRef(null);
  if (!uploaderRef.current) {
    // Extend video mime types to include audio for voice memos
    const customVideoTypes = new Set([
      ...DEFAULT_VIDEO_MIME_TYPES,
      'audio/mpeg',
      'audio/mpeg-3',
      'audio/mp3',
    ]);
    const customIsVideoName = (name = '') =>
      /\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv|mp3)$/i.test(name);

    uploaderRef.current = createMediaUploader({
      maxImageBytes: MAX_IMAGE_SIZE,
      maxVideoBytes: MAX_VIDEO_SIZE,
      videoMimeTypes: customVideoTypes,
      isVideoName: customIsVideoName,
      forceAutoResourceType: true,
      enableAdaptiveLadder: false, // keep lean for chat
      enableGifOptimization: true,
      enableThumbnails: false, // optional for chat – disable to save time
      enableHashDedupe: true,
      debug: false,
      signed: false, // unsigned preset
    });
  }
  const uploader = uploaderRef.current;

  const chatMessages = Array.isArray(messages) ? messages : [];
  const canViewGalaxyChat = listIncludesPlayer(galaxyMembers, senderId);
  const typing = useGalaxyTyping({ socket, galaxyId, actorId: senderId, token: player?.token,
    active: isRadio && active && canViewGalaxyChat && isGalaxyMember,
    canType: !loading && !sendingFile && !voiceMemo && !permissionPending });
  const showHistoryLoading =
    subscriptionStatus === 'Subscribed' && isChatHistoryLoading;
  const showHistoryError =
    subscriptionStatus === 'Subscribed' &&
    Boolean(chatHistoryError) &&
    chatMessages.length === 0;
  const showEmptyHistory =
    subscriptionStatus === 'Subscribed' &&
    isChatHistoryLoaded &&
    !showHistoryLoading &&
    !chatHistoryError &&
    chatMessages.length === 0;
  const { start: visibleStart, showMore, jumpToLatest, away } = useGalaxyChatScroll({
    messages: chatMessages,
    scope: `${readingScope}:${canViewGalaxyChat}`,
    active: canViewGalaxyChat,
    renderingMessages: active && !showHistoryLoading && !showHistoryError,
    viewportRef: chatContainerRef,
    contentRef: chatContentRef,
  });

  const handleClickOutside = () => {
    setPicker((prev) => !prev);
  };

  useClickOutside(menu, () => handleClickOutside());

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    !mobileView ? (voiceMemo ? messageRef : textRef) : messageRef,
    mobileView,
    '+1 JGK So Fun!',
    MESSAGE_PARTICLE_COUNT,
    'messenger',
    voiceMemo,
  );

  useEffect(() => {
    if (cursorPosition && textRef !== null)
      textRef.current.selectionEnd = cursorPosition;
  }, [cursorPosition]);

  useEffect(() => {
    const newOfflineMembers = galaxyMembers.filter(
      (memberId) =>
        !galaxyChatOnlinePlayers.some(
          (chatPlayer) => chatPlayer.id === memberId,
        ),
    );
    setOfflineMembers(newOfflineMembers);
  }, [galaxyChatOnlinePlayers, galaxyMembers]);

  useEffect(() => {
    if (!isRadio && !messageSoundRef.current) {
      messageSoundRef.current = new Audio(INCOMING_MESSAGE_BL_URL);
    }

    if (!active || !canViewGalaxyChat) {
      return;
    }

    const release = acquireGalaxyChat({ socket, galaxyId, playerId: senderId, token: player?.token,
      onReconnect: onRetryChatHistory, onRevoked: onAccessDenied });

    const handleNewMessage = (data) => {
      if (data?.galaxyId !== galaxyId || !data._id) {
        return;
      }
      setMessages((prevMessages) => reconcileGalaxyMessage(
        prevMessages, { ...data, isPending: false },
      ));

      if (!isRadio && data.senderId !== senderId) {
        const playPromise = messageSoundRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log(error);
          });
        }
      }
    };

    socket.on('getGalaxyChatMessage', handleNewMessage);
    const handleBulkDelete = (data) => {
      if (!isRadio && data?.galaxyId === galaxyId) setMessages([]);
    };
    socket.on('getGalaxyMessagesDeleted', handleBulkDelete);

    return () => {
      release();
      socket.off('getGalaxyChatMessage', handleNewMessage);
      socket.off('getGalaxyMessagesDeleted', handleBulkDelete);
    };
  }, [galaxyId, senderId, canViewGalaxyChat, active, isRadio, player?.token, onRetryChatHistory, onAccessDenied, setMessages]);

  const handleMicClick = () => {
    if (isRadio) {
      if (recorder.isCaptureBusy()) stopRecording();
      else { setVoiceMemo(true); void startRecording(); }
      return;
    }
    setVoiceMemo(true);
    if (!isRecording) {
      startRecording();
    } else {
      stopRecording();
    }
    setIsRecording((prev) => !prev);
  };

  const persistMessage = async (message, scope) => {
    if (chatScopeRef.current !== scope || (isRadio && (!activeRef.current || !isGalaxyMember))) return false;
    setMessages((previous) => reconcileGalaxyMessage(
      previous, { ...message, isPending: true },
    ));
    try {
      const response = await axiosConn.post(
        'galaxyMessages', { message, offlineMembers },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      const saved = response?.data?.data?.data;
      if (!saved?._id || saved.galaxyId !== message.galaxyId) {
        throw new Error('Message status is uncertain. Refresh chat before sending again.');
      }
      if (chatScopeRef.current !== scope) return false;
      setMessages((previous) => reconcileGalaxyMessage(
        previous, { ...saved, isPending: false },
      ));
      return true;
    } catch (err) {
      if (chatScopeRef.current === scope && [401, 403].includes(err?.response?.status)) onAccessDenied?.();
      if (chatScopeRef.current === scope) {
        setMessages((previous) => (Array.isArray(previous) ? previous : []).filter(
          (entry) => !(entry.isPending && entry.tempId === message.tempId),
        ));
      }
      throw err;
    }
  };

  const sendError = (err) => err.response?.status === 403
    ? 'You no longer have access to this Galaxy chat. Refresh to check your membership.'
    : err.response?.status === 401
      ? 'Your session has expired. Sign in again to send this message.'
      : err.response?.data?.message || err.message || 'Could not send message.';

  // ============ NEW: Use mediaUpload utility for audio (voice memo) ============
  const handleSendAudioMemo = async () => {
    if (chatScopeRef.current !== chatScope) return;
    if (!listIncludesPlayer(galaxyMembers, senderId) || sendingRef.current) return;

    if (!mediaBlobUrl) {
      setVoiceMemo(false);
      return;
    }

    jumpToLatest();
    let transformedUrl = '';
    const scope = chatScopeRef.current;
    sendingRef.current = true;
    setError('');
    try {
      setLoading(true);
      setProgress(0);
      setUploadPercentage(0);

      const audioBlob = await fetch(mediaBlobUrl).then((r) => r.blob());
      const fileName = Date.now() + '.mp3';
      const audioFile = new File([audioBlob], fileName, { type: 'audio/mpeg' });
      if (chatScopeRef.current !== scope) return;

      // Validate (will throw if unsupported)
      uploader.validate(audioFile);

      const result = await uploader.upload(audioFile, {
        publicIdPrefix: 'GalaxyChat/Audio',
        onProgress: (pct) => {
          if (chatScopeRef.current !== scope) return;
          setProgress(pct);
          setUploadPercentage(pct);
        },
        transform: true,
      });

      if (chatScopeRef.current !== scope) return;
      transformedUrl = result.transformedUrl;
      setProgress(100);
      setUploadPercentage(100);
    } catch (e) {
      if (chatScopeRef.current !== scope) return;
      setLoading(false);
      setError(e.message || 'Audio upload failed.');
      sendingRef.current = false;
      return;
    }

    const message = {
      galaxyId,
      senderId: player.data.player._id,
      text: messageText,
      img: transformedUrl,
      senderProfilePhoto: player.data.player.profilePhoto,
      senderPlayerId: player.data.player.playerId,
      createdAt: new Date(),
      tempId: uuidv4(),
    };

    try {
      if (!(await persistMessage(message, scope))) return;
      setMessageText('');
      setFile(null);
      if (fileInput.current) fileInput.current.value = null;
      if (!isRadio) showConfettiText();
      setVoiceMemo(false);
      clearBlobUrl();
    } catch (err) {
      if (chatScopeRef.current === scope) setError(sendError(err));
    } finally {
      if (chatScopeRef.current !== scope) return;
      setLoading(false);
      sendingRef.current = false;
    }
  };

  // ============ NEW: Use mediaUpload utility for image/video attachments ============
  const handleSubmit = async (e) => {
    if (sendingRef.current || chatScopeRef.current !== chatScope) return;
    if (
      !listIncludesPlayer(galaxyMembers, senderId) &&
      subscriptionStatus !== 'Subscribed'
    ) {
      return;
    }

    if (
      (e.key === 'Enter' || e.type === 'click') &&
      (messageText.trim() !== '' || file) // CHANGED: allow send when file state present
    ) {
      sendingRef.current = true;
      setLoading(true);
      setError('');
      let transformedUrl = '';
      const scope = chatScopeRef.current;

      if (file) {
        try {
          setLoading(true);
          setSendingFile(true);
          setProgress(0);
          setUploadPercentage(0);

          // Validate via uploader (size/type gates)
          uploader.validate(file);

          const { transformedUrl: tUrl } = await uploader.upload(file, {
            publicIdPrefix: 'GalaxyChat/Media',
            onProgress: (pct) => {
              if (chatScopeRef.current !== scope) return;
              setProgress(pct);
              setUploadPercentage(pct);
            },
            transform: true,
          });

          if (chatScopeRef.current !== scope) return;
          transformedUrl = tUrl;
        } catch (e) {
          if (chatScopeRef.current !== scope) return;
          sendingRef.current = false;
          setLoading(false);
          setSendingFile(false);
          if (e instanceof MediaUploadError) {
            setError(e.message);
          } else {
            setError(e.message || 'Upload failed.');
          }
          return;
        }
      }

      const message = {
        galaxyId,
        senderId: player.data.player._id,
        text: messageText.trim(),
        img: transformedUrl,
        senderProfilePhoto: player.data.player.profilePhoto,
        senderPlayerId: player.data.player.playerId,
        createdAt: new Date(),
        tempId: uuidv4(),
      };

      try {
        if (!(await persistMessage(message, scope))) return;

        setLoading(false);
        setMessageText('');
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        setSendingFile(false);
        setProgress(100);
        setUploadPercentage(100);
        if (!isRadio) showConfettiText();
      } catch (err) {
        if (chatScopeRef.current === scope) setError(sendError(err));
      } finally {
        if (chatScopeRef.current !== scope) return;
        setLoading(false);
        setSendingFile(false);
        sendingRef.current = false;
      }
    }
  };

  const debouncedHandleSubmit = debounce(handleSubmit, 700);
  // Capture deliberate Send intent now, before debounce/upload. Later pending,
  // HTTP and socket updates follow the reader's most recent scroll intent.
  const submitFromComposer = (event, deferred = false) => {
    typing.stop();
    if (!sendingRef.current && chatScopeRef.current === chatScope &&
      canViewGalaxyChat && (event.key === 'Enter' || event.type === 'click') &&
      (messageText.trim() || file)) jumpToLatest();
    return deferred ? debouncedHandleSubmit(event) : handleSubmit(event);
  };

  const handleDeleteMessage = (messageId) => {
    setMessages((previous) =>
      (Array.isArray(previous) ? previous : []).filter(
        (m) => m._id !== messageId && m.tempId !== messageId,
      ),
    );
  };

  const mediaControllers = useRef(new Set());
  const mediaPolicy = useRef(null);
  mediaPolicy.current = { active, scope: chatScope, recorder, onBeforeCapture };
  const stopChatMedia = useCallback((except) => {
    mediaControllers.current.forEach((controller) => { if (controller !== except) controller.pause(); });
    rootRef.current?.querySelectorAll('audio,video').forEach((media) => {
      if (!except?.contains(media)) media.pause();
    });
  }, []);
  const mediaOwner = useMemo(() => ({
    register: (controller) => {
      mediaControllers.current.add(controller);
      return () => mediaControllers.current.delete(controller);
    },
    requestPlay: (controller) => {
      const policy = mediaPolicy.current;
      if (!policy.active || policy.scope !== chatScope || !chatScopeRef.current || policy.recorder.isCaptureBusy()) return false;
      stopChatMedia(controller);
      policy.onBeforeCapture?.();
      return true;
    },
  }), [chatScope, stopChatMedia]);
  const canHide = () => {
    if (recorder.isCaptureBusy()) {
      setError('Stop or cancel the microphone before returning to Radio.');
      return false;
    }
    stopChatMedia();
    return true;
  };
  useImperativeHandle(ref, () => ({ prepareToHide: canHide, stopChatMedia,
    dismissForeground: () => {
      if (picker) { setPicker(false); return true; }
      return middleRef.current?.dismissPreview() || false;
    },
  }));
  useLayoutEffect(() => {
    if (!isRadio) return undefined;
    return () => {
      chatScopeRef.current = '';
      uploader.cancel?.();
      stopChatMedia();
    };
  }, [isRadio, uploader, stopChatMedia]);
  useEffect(() => {
    if (isRadio && !active) {
      stopChatMedia(); uploader.cancel?.();
      sendingRef.current = false; setLoading(false); setSendingFile(false);
    }
  }, [active, isRadio, uploader, stopChatMedia]);
  useEffect(() => { if (isRadio && captureError) setVoiceMemo(false); }, [isRadio, captureError]);
  const onChatPlay = (event) => {
    const media = event.target;
    if (!isRadio || !['AUDIO', 'VIDEO'].includes(media.tagName)) return;
    if (media.closest('[data-chat-media-player]')) return; // ReactPlayer callbacks own this media.
    if (event.type === 'volumechange' && media.paused) return;
    if (!active || recorder.isCaptureBusy()) { media.pause(); return; }
    if (media.muted || media.volume === 0) return;
    mediaControllers.current.forEach((controller) => controller.pause());
    rootRef.current?.querySelectorAll('audio,video').forEach((other) => { if (other !== media) other.pause(); });
    onBeforeCapture?.();
  };
  const middle = (
        <ChatBoxMiddle ref={middleRef} dismissiblePreview={isRadio}
          showFileSend={!isRadio}
          autoClearOnSend={!isRadio}
          isTypingDisplay={isTypingDisplay}
          senderPlayerId={senderPlayerId}
          status={permissionPending ? 'waiting for microphone permission' : status}
          mediaBlobUrl={mediaBlobUrl}
          loading={loading}
          handleMicClick={handleMicClick}
          isRecording={isRadio ? recorder.isCaptureBusy() : isRecording}
          mobileView={mobileView}
          width={width}
          setVoiceMemo={setVoiceMemo}
          clearBlobUrl={isRadio ? cancelRecording : clearBlobUrl}
          handleSendAudioMemo={handleSendAudioMemo}
          fileInput={fileInput}
          attachChatFile={attachChatFile}
          clearChatAttachment={clearChatAttachment}
          handleFileInput={(e) =>
            handleFileInput(e, setError, setFile, maxVideoSize, maxImageSize)
          }
          error={error || captureError}
          setError={setError}
          setFile={setFile}
          setText={setMessageText}
          file={file}
          setLoading={setLoading}
          setIsLoading={setIsLoading}
          setSendingFile={setSendingFile}
          messageRef={messageRef}
          chatContainerRef={chatContainerRef}
          handleSubmit={submitFromComposer}
        />
  );

  return (
    <GalaxyChatMediaContext.Provider value={isRadio ? mediaOwner : null}>
    <div ref={rootRef} className={isRadio ? 'radio-conversation-chat' : undefined}
      style={isRadio ? undefined : { display: 'contents' }}
      onPlayCapture={onChatPlay} onVolumeChangeCapture={onChatPlay}>
      {!isRadio && (subscriptionStatus === 'Subscribe' ||
        subscriptionStatus === 'Requested') && (
        <>
          <div className="centered-column-galaxyChatBox">
            <WelcomeMessage
              subscriptionStatus={subscriptionStatus}
              playerId={player.data.player.playerId}
              isPlayerInRole={isPlayerInRole}
              onSubscribe={onSubscribe}
              onCancelRequest={onCancelRequest}
              onUnsubscribe={onUnsubscribe}
              membershipButtonLabel={membershipButtonLabel}
              className={className}
              galaxyName={galaxyName}
            />
            {subscriptionStatus === 'Subscribed' && (
              <AdRotator surfaceKey="chat" />
            )}
          </div>
        </>
      )}
      <div className="galaxy-chat-viewport-shell">
      <div className="chat-container" ref={chatContainerRef}>
      <div className="galaxy-chat-content" ref={chatContentRef}>
        {subscriptionStatus === 'Subscribed' &&
          visibleStart > 0 && (
            <div
              className={
                !mobileView
                  ? 'viewMessages load-more-messages'
                  : 'viewMessagesMobile load-more-messages'
              }
              onClick={() => showMore()}
            >
              View {visibleStart} more messages
            </div>
          )}
        {showHistoryLoading && (
          <div className="galaxy-chat-state" role="status">
            <p className="galaxy-chat-state__title">Loading chat history...</p>
          </div>
        )}
        {showHistoryError && (
          <div className="galaxy-chat-state galaxy-chat-state--error">
            <p className="galaxy-chat-state__title">
              Could not load chat history.
            </p>
            <p className="galaxy-chat-state__body">
              Your composer still works, but the previous messages need a
              refresh.
            </p>
            {typeof onRetryChatHistory === 'function' && (
              <button
                type="button"
                className="galaxy-chat-state__retry"
                onClick={onRetryChatHistory}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {showEmptyHistory && (
          <div className="galaxy-chat-state">
            <p className="galaxy-chat-state__title">
              No Galaxy messages yet.
            </p>
            <p className="galaxy-chat-state__body">
              Start the first conversation for this Galaxy.
            </p>
          </div>
        )}
        {!showHistoryLoading &&
          !showHistoryError &&
          chatMessages
            .slice(visibleStart)
            .map((message) => {
              const isOwnMessage = message.senderId === senderId;
              return (
                canViewGalaxyChat && (
                  <div
                    key={message._id || message.tempId}
                    data-galaxy-scroll-id={galaxyScrollIdentity(message)}
                    className={isOwnMessage ? 'message-own' : 'message-other'}
                  >
                    <Message
                      message={message}
                      variant={variant}
                      active={active}
                      own={isOwnMessage}
                      senderProfilePhoto={message.senderProfilePhoto}
                      from="galaxyChat"
                      onDeleteMessage={handleDeleteMessage}
                      galaxyId={galaxyId}
                      isPlayerInRole={isPlayerInRole}
                    />
                    {message.isPending && <span role="status">Sending...</span>}
                  </div>
                )
              );
            })}
        <div className="bottomMessages" />
        <div className="chat-input">
          {!isRadio && <ConfettiTextComponent />}
        </div>
        {!isRadio && middle}

      </div>
      </div>
      {away && (
        <button type="button" className="galaxy-chat-jump" onClick={jumpToLatest}>
          Jump to latest
        </button>
      )}
      </div>

      <div className="chatBoxBottomContainer">
        {isRadio && <TypingIndicator variant="radio" players={typing.players} />}
        {isRadio && middle}
        {!voiceMemo && isGalaxyMember && (
          <ChatBoxBottom
            voiceMemo={voiceMemo}
            picker={picker}
            menu={menu}
            handleEmoji={(_event, data) => {
              const emoji = data?.emoji;
              if (typeof emoji !== 'string') return;
              const input = textRef.current;
              const replaced = (input?.selectionEnd ?? messageText.length) - (input?.selectionStart ?? messageText.length);
              if (messageText.length - replaced + emoji.length > 11000) return;
              handleEmoji(emoji, textRef, messageText, setMessageText, setCursorPosition);
            }}
            textRef={textRef}
            register={register}
            text={messageText}
            setText={setMessageText}
            handleSubmit={(event) => submitFromComposer(event, true)}
            loading={loading}
            sendingFile={sendingFile}
            handleMicClick={handleMicClick}
            isRecording={isRecording}
            setPicker={setPicker}
            fileInput={fileInput}
            attachChatFile={attachChatFile}
            setSendingFile={setSendingFile}
            handleSendAudioMemo={handleSendAudioMemo}
            parent="GalaxyChatBox"
            variant={isRadio ? 'radio' : undefined}
            hasAttachment={Boolean(file)}
            recordReference={recordReference}
            onTypingActivity={isRadio ? typing.activity : undefined}
            onTypingStop={isRadio ? typing.stop : undefined}
          />
        )}
      </div>
    </div>
    </GalaxyChatMediaContext.Provider>
  );
});

export default GalaxyChatBox;
